import { NotificationSchema, type Notification as WireNotification } from '@contracts/schemas/activity.schema';
import { pageOf } from '@contracts/schemas/common.schema';
import {
  NotificationSummarySchema,
  PushNotificationsSchema,
} from '@contracts/schemas/preferences.schema';
import {
  NOTIFICATION_CATEGORIES,
  notificationCategoryOf,
  notificationTitleOf,
  type NotificationCategory,
} from '@/domain';
import type {
  Notification,
  NotificationFilter,
  NotificationRepository,
  NotificationSummary,
} from '@/application/ports/NotificationRepository';
import { request } from '../client';

const NotificationPageSchema = pageOf(NotificationSchema);

/**
 * A row as the screens read it. The category and the title come with the row;
 * an API from before the two is read by the type alone, as the server would.
 */
function fromWire(row: WireNotification): Notification {
  return {
    id: row.id,
    type: row.type,
    category: row.category ?? notificationCategoryOf(row.type),
    title: row.title ?? notificationTitleOf(row.type),
    text: row.text,
    createdAt: row.createdAt,
    read: row.read,
    push: row.push ?? false,
    target: row.target,
  };
}

export class HttpNotificationRepository implements NotificationRepository {
  async list(filter: NotificationFilter = {}): Promise<Notification[]> {
    const query = new URLSearchParams({ pageSize: String(filter.pageSize ?? 100) });
    if (filter.category !== undefined) query.set('category', filter.category);
    if (filter.unread === true) query.set('unread', 'true');
    if (filter.oldestFirst === true) query.set('sort', 'oldest');
    if (filter.home === true) query.set('home', 'true');
    if (filter.important === true) query.set('important', 'true');

    const page = await request(`/notifications?${query.toString()}`, {
      schema: NotificationPageSchema,
    });
    return page.items.map(fromWire);
  }

  async summary(): Promise<NotificationSummary> {
    const counted = await request('/notifications/summary', { schema: NotificationSummarySchema });
    const byCategory = {} as Record<NotificationCategory, number>;
    for (const category of NOTIFICATION_CATEGORIES) byCategory[category] = counted.byCategory[category] ?? 0;
    return { unread: counted.unread, byCategory };
  }

  async push(after: string | null): Promise<{ items: Notification[]; next: string | null }> {
    const query = after === null ? '' : `?after=${encodeURIComponent(after)}`;
    const answer = await request(`/notifications/push${query}`, { schema: PushNotificationsSchema });
    return { items: answer.items.map(fromWire), next: answer.next ?? null };
  }

  async markRead(id: string): Promise<void> {
    await request(`/notifications/${id}/read`, { method: 'POST' });
  }

  async markAllRead(): Promise<void> {
    await request('/notifications/read-all', { method: 'POST' });
  }
}
